import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  disableManagedDomainCertificate,
  enableManagedDomainCertificate,
  getDomainCertificate,
  listDomainCertificates,
  refreshDomainCertificates,
  renewDomainCertificate,
  type EnableManagedDomainCertificateInput,
  type ListDomainCertificatesParams,
} from '../api/endpoints/domain-certificates.ts';

export const domainCertificateKeys = {
  all: ['domain-certificates'] as const,
  list: (params?: ListDomainCertificatesParams) =>
    [...domainCertificateKeys.all, 'list', params ?? {}] as const,
  detail: (idOrDomain: string) =>
    [...domainCertificateKeys.all, 'detail', idOrDomain] as const,
};

export function useDomainCertificates(params?: ListDomainCertificatesParams) {
  return useQuery({
    queryKey: domainCertificateKeys.list(params),
    queryFn: () => listDomainCertificates(params),
  });
}

export function useDomainCertificate(idOrDomain: string) {
  return useQuery({
    queryKey: domainCertificateKeys.detail(idOrDomain),
    queryFn: () => getDomainCertificate(idOrDomain),
    enabled: !!idOrDomain,
  });
}

export function useRefreshDomainCertificates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params?: { domain?: string }) => refreshDomainCertificates(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: domainCertificateKeys.all });
    },
  });
}

export function useEnableManagedDomainCertificate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { idOrDomain: string } & EnableManagedDomainCertificateInput) => {
      const { idOrDomain, ...input } = vars;
      return enableManagedDomainCertificate(idOrDomain, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: domainCertificateKeys.all });
    },
  });
}

export function useDisableManagedDomainCertificate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (idOrDomain: string) => disableManagedDomainCertificate(idOrDomain),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: domainCertificateKeys.all });
    },
  });
}

export function useRenewDomainCertificate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { idOrDomain: string; force?: boolean }) =>
      renewDomainCertificate(vars.idOrDomain, { force: vars.force }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: domainCertificateKeys.all });
    },
  });
}
